import React, { useState } from "react";

// Pass 46/47 — Reusable action block embedded in the lead drawer (team
// + admin). Shows the personal-line input, channel-aware Send
// buttons, and Skip. After action, calls `onActionComplete(updated)`
// so the drawer can refresh + parent surfaces (leads list, dashboard
// pending list) can update.
//
// Channel availability is derived from the lead's contact fields:
//   - whatsapp available iff Customer_Phone present
//   - email available iff Customer_Email present
//
// IMPORTANT: this block must remain ACTIVE even when the surrounding
// drawer has put the rest of the form into read-only mode (e.g.
// team-side closed-won leads). Sending a review on your own closed
// deal is a permitted action; the lock applies only to status/note
// edits.
//
// Pass 47 changes:
//  - Email path is now PREVIEW-GATED. First click on "Send via Email"
//    fetches a server-rendered preview (subject + html + recipient
//    summary including the salesperson BCC) and displays it inline.
//    The actual send happens only after the salesperson clicks
//    "Confirm and send" in the preview pane.
//  - WhatsApp path is UNCHANGED — the wa.me deep link already
//    surfaces the full message in WhatsApp before the salesperson
//    taps send, so a second app-side preview would be redundant.
//  - Explicit success ("Review request emailed to {customer}") and
//    error feedback render in-place so the operator no longer relies
//    on "the block self-hid" as their only signal.

export default function ReviewActionBlock({ lead, apiClient, onActionComplete }) {
  const [personal, setPersonal] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");
  const [emailPreview, setEmailPreview] = useState(null); // { to, bcc, reply_to, subject, html, customer_name }

  // Derive flags inline so the block works regardless of whether the
  // caller passed a decorated row (review_pending set by the server)
  // or a raw one.
  //
  // Pass 55 — this was an `||`, which inverted the intent: the server
  // correctly sends review_pending:false for a New / In Progress /
  // Closed Lost lead, but the fallback clause then matched because
  // Review_Status is empty on any lead that never had a review
  // actioned, so the block rendered "Closed-won within the last 14
  // days" on practically every lead. The server's answer is
  // authoritative whenever it is present; the Review_Status heuristic
  // is only a fallback for a caller that doesn't supply it.
  const pending = typeof lead.review_pending === "boolean"
    ? lead.review_pending
    : (lead.Review_Status || "").toLowerCase() === "";
  const hasPhone = lead.has_phone === true
    || !!(lead.Customer_Phone || "").trim();
  const hasEmail = lead.has_email === true
    || !!(lead.Customer_Email || "").trim();
  const hasLink = !!(lead.review_link || "");

  // After a successful action the row no longer satisfies the review
  // predicate. Keep the success banner visible (it lives outside the
  // pending-gated body) so the operator sees the confirmation even
  // though everything else collapsed.
  if (!pending) {
    if (!success) return null;
    return (
      <div
        data-testid="drawer-review-success"
        style={{
          marginBottom: 22, padding: 14,
          background: "#eaf5ea", border: "1px solid #b9dcb4",
          color: "#2f5e3d", fontSize: 13, lineHeight: 1.5,
        }}
      >
        {success}
      </div>
    );
  }

  const guarded = async (fn) => {
    if (busy) return;
    setErr(""); setBusy(true);
    try { await fn(); }
    catch (e) {
      setErr(e?.response?.data?.detail || "Action failed.");
      setBusy(false);
    }
  };

  const sendWhatsapp = () => guarded(async () => {
    const r = await apiClient.reviewSend(lead.ID, {
      channel: "whatsapp", personal_line: personal,
    });
    if (r.data?.whatsapp_url) {
      window.open(r.data.whatsapp_url, "_blank", "noopener,noreferrer");
    }
    setSuccess(`WhatsApp draft opened for ${lead.Customer_Name}.`);
    onActionComplete?.(r.data?.lead);
  });

  // Step 1 of email flow — fetch the rendered preview (no send yet).
  const previewEmail = () => guarded(async () => {
    const r = await apiClient.reviewEmailPreview(lead.ID, { personal_line: personal });
    setEmailPreview(r.data);
    setBusy(false);
  });

  // Step 2 of email flow — confirm + actually send.
  const confirmSendEmail = () => guarded(async () => {
    const r = await apiClient.reviewSend(lead.ID, {
      channel: "email", personal_line: personal,
    });
    const recipient = r.data?.emailed_to || emailPreview?.to || lead.Customer_Email;
    setSuccess(`Review request emailed to ${recipient}.`);
    setEmailPreview(null);
    onActionComplete?.(r.data?.lead);
  });

  const backToEdit = () => {
    setEmailPreview(null);
    setErr("");
  };

  const skip = () => guarded(async () => {
    const r = await apiClient.reviewSkip(lead.ID);
    setSuccess("Review request skipped.");
    onActionComplete?.(r.data?.lead);
  });

  // Preview pane — replaces the action body once the salesperson
  // clicks "Send via Email". Renders the subject, the full HTML in a
  // sandboxed-feeling block (style scoping limited to inline styles
  // already in the template), and a recipients summary so the
  // salesperson can verify the BCC contract before committing.
  if (emailPreview) {
    return (
      <div
        data-testid="drawer-review-email-preview"
        style={{
          marginBottom: 22, padding: 16,
          background: "#fff7e6", border: "1px solid #f0d9a1",
        }}
      >
        <p className="scw-eyebrow" style={{ marginBottom: 6, color: "#7a5a13" }}>
          Email preview — confirm before sending
        </p>
        <div style={{ background: "#fff", border: "1px solid #ecdfb3", padding: 14, marginBottom: 14 }}>
          <p style={{ margin: "0 0 6px 0", fontSize: 12, color: "#7a5a13", letterSpacing: "0.12em", textTransform: "uppercase" }}>
            To
          </p>
          <p style={{ margin: "0 0 10px 0", fontSize: 14 }} data-testid="email-preview-to">
            {emailPreview.to}
          </p>
          {emailPreview.bcc && emailPreview.bcc.length > 0 && (
            <>
              <p style={{ margin: "0 0 6px 0", fontSize: 12, color: "#7a5a13", letterSpacing: "0.12em", textTransform: "uppercase" }}>
                Bcc (silent copy)
              </p>
              <p style={{ margin: "0 0 10px 0", fontSize: 13, color: "#5C5C5C" }} data-testid="email-preview-bcc">
                {emailPreview.bcc.join(", ")}
              </p>
            </>
          )}
          {emailPreview.reply_to && (
            <>
              <p style={{ margin: "0 0 6px 0", fontSize: 12, color: "#7a5a13", letterSpacing: "0.12em", textTransform: "uppercase" }}>
                Reply-To
              </p>
              <p style={{ margin: "0 0 10px 0", fontSize: 13, color: "#5C5C5C" }} data-testid="email-preview-replyto">
                {emailPreview.reply_to}
              </p>
            </>
          )}
          <p style={{ margin: "0 0 6px 0", fontSize: 12, color: "#7a5a13", letterSpacing: "0.12em", textTransform: "uppercase" }}>
            Subject
          </p>
          <p style={{ margin: "0 0 14px 0", fontSize: 14, fontWeight: 500 }} data-testid="email-preview-subject">
            {emailPreview.subject}
          </p>
          <p style={{ margin: "0 0 6px 0", fontSize: 12, color: "#7a5a13", letterSpacing: "0.12em", textTransform: "uppercase" }}>
            Body
          </p>
          {/*
            Security Phase 1 (26 Sep 2026). This used to inject the email
            HTML straight into the page with dangerouslySetInnerHTML, on
            the belief that no user-typed HTML could reach it. That was
            false: the template interpolated the customer's name and
            product unescaped, so a hostile name would have run as script
            inside the Hub, with the viewer's session. The server now
            escapes every template value; this is the second layer.

            sandbox="" (no allow-scripts, no allow-same-origin) means that
            even if something unescaped slipped through a future template
            edit, it cannot execute or reach the Hub's cookies or API.
            srcDoc renders the email as its own document, which is also a
            more faithful preview than splicing <html> into a <div>.
          */}
          <iframe
            data-testid="email-preview-body"
            title="Email preview"
            sandbox=""
            srcDoc={emailPreview.html}
            style={{
              display: "block", width: "100%", height: 360,
              border: "1px solid var(--scw-line)",
              background: "#fafafa",
            }}
          />
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <button
            type="button"
            className="scw-btn scw-btn-primary"
            onClick={confirmSendEmail}
            disabled={busy}
            style={{ minHeight: 44, flex: "1 1 200px" }}
            data-testid="drawer-review-confirm-send-email"
          >
            {busy ? "Sending\u2026" : "Confirm and send"}
          </button>
          <button
            type="button"
            className="scw-btn scw-btn-ghost"
            onClick={backToEdit}
            disabled={busy}
            style={{ minHeight: 44, flex: "0 0 auto" }}
            data-testid="drawer-review-back-to-edit"
          >
            Back to edit
          </button>
        </div>
        {err && (
          <p
            data-testid="drawer-review-error"
            style={{ color: "#a13b3b", fontSize: 13, marginTop: 10 }}
          >
            {err}
          </p>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid="drawer-review-block"
      style={{
        marginBottom: 22, padding: 16,
        background: "#fff7e6", border: "1px solid #f0d9a1",
      }}
    >
      <p className="scw-eyebrow" style={{ marginBottom: 6, color: "#7a5a13" }}>
        Send review request
      </p>
      <p style={{ margin: "0 0 12px 0", fontSize: 13, color: "#5C5C5C", lineHeight: 1.5 }}>
        Closed-won within the last 14 days. Send a Google-review nudge from your
        own WhatsApp or email.
      </p>
      <label className="scw-label" htmlFor={`pl-${lead.ID}`}>Personal line (optional)</label>
      <input
        id={`pl-${lead.ID}`}
        className="scw-input"
        value={personal}
        onChange={(e) => setPersonal(e.target.value)}
        placeholder="e.g. Loved how the strap pairs with the dial."
        style={{ marginBottom: 12 }}
        data-testid="drawer-review-personal"
      />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {hasPhone && (
          <button
            type="button"
            className="scw-btn scw-btn-primary"
            onClick={sendWhatsapp}
            disabled={busy}
            style={{ minHeight: 44, flex: "1 1 140px" }}
            data-testid="drawer-review-send-whatsapp"
          >
            Send via WhatsApp
          </button>
        )}
        {hasEmail && (
          <button
            type="button"
            className="scw-btn scw-btn-ghost"
            onClick={previewEmail}
            disabled={busy}
            style={{ minHeight: 44, flex: "1 1 140px" }}
            data-testid="drawer-review-send-email"
          >
            {busy ? "Loading preview\u2026" : "Send via Email"}
          </button>
        )}
        <button
          type="button"
          className="scw-btn scw-btn-ghost"
          onClick={skip}
          disabled={busy}
          style={{ minHeight: 44, flex: "0 0 auto", color: "var(--scw-muted)" }}
          data-testid="drawer-review-skip"
        >
          Skip
        </button>
      </div>
      {!hasLink && (
        <p style={{ marginTop: 10, fontSize: 12, color: "#7a5a13", lineHeight: 1.5 }}>
          Tip: paste the store{"\u2019"}s Google review URL in Admin Settings so the
          customer{"\u2019"}s link is included.
        </p>
      )}
      {err && (
        <p
          data-testid="drawer-review-error"
          style={{ color: "#a13b3b", fontSize: 13, marginTop: 10 }}
        >
          {err}
        </p>
      )}
    </div>
  );
}
